import { Check } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Button } from "../components/Button";
import { Container } from "../components/Container";
import { PhoneMockup } from "../components/PhoneMockup";
import { useLocale } from "../hooks/useLocale";

const BULLET_KEYS = ["bullet_1", "bullet_2", "bullet_3", "bullet_4"] as const;

const GUIDE_SCREENS = [
  "/guide-pics/1a..png",
  "/guide-pics/2a..png",
  "/guide-pics/1g.png",
  "/guide-pics/2g..png",
];

const GUIDE_SCREEN_STYLES = {
  "/guide-pics/1g.png": {
    objectFit: "contain",
    objectPosition: "top center",
  },
} as const;

export function ForAgencies() {
  const { t } = useTranslation();
  const { pathFor } = useLocale();
  const reduce = useReducedMotion();

  const fadeUp = (delay = 0) =>
    reduce
      ? { initial: false }
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-100px" },
          transition: { duration: 0.55, ease: "easeOut" as const, delay },
        };

  return (
    <section
      id="for-agencies"
      className="bg-bg-subtle py-14 dark:bg-bg-muted sm:py-20"
    >
      <Container>
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">

          {/* Left — phone mockup */}
          <motion.div
            {...fadeUp(0)}
            className="order-2 flex items-center justify-center lg:order-1"
            dir="ltr"
          >
            <PhoneMockup
              screens={GUIDE_SCREENS}
              screenStyles={GUIDE_SCREEN_STYLES}
              tall
            />
          </motion.div>

          {/* Right — copy */}
          <div className="order-1 lg:order-2">
            <motion.p
              {...fadeUp(0)}
              className="text-base font-bold uppercase tracking-[0.25em] text-brand-500"
            >
              {t("for_agencies.eyebrow")}
            </motion.p>

            <motion.h2
              {...fadeUp(0.05)}
              className="mt-4 text-4xl font-extrabold leading-tight tracking-tight text-text-heading sm:text-5xl"
            >
              {t("for_agencies.title_line1")}
              <br />
              {t("for_agencies.title_line2_plain")}{" "}
              <span className="text-brand-500">
                {t("for_agencies.title_line2_highlight")}
              </span>
              <br />
              <span className="text-brand-500">
                {t("for_agencies.title_line3")}
              </span>
            </motion.h2>

            <motion.p
              {...fadeUp(0.1)}
              className="mt-5 text-base leading-relaxed text-text-muted"
            >
              {t("for_agencies.description")}{" "}
              <strong className="font-semibold text-text-heading">
                {t("for_agencies.description_bold")}
              </strong>
            </motion.p>

            <motion.ul
              {...fadeUp(0.15)}
              className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2"
            >
              {BULLET_KEYS.map((key) => (
                <li key={key} className="flex items-start gap-2.5">
                  <Check
                    className="mt-0.5 h-4 w-4 flex-none text-brand-500"
                    aria-hidden="true"
                    strokeWidth={3}
                  />
                  <span className="text-sm text-text-body">
                    {t(`for_agencies.${key}`)}
                  </span>
                </li>
              ))}
            </motion.ul>

            <motion.div {...fadeUp(0.2)} className="mt-10">
              <Button as="a" href={pathFor("/waitlist")} variant="primary" size="lg">
                {t("nav.join_waitlist", { defaultValue: "Join waitlist" })}
              </Button>
              <p className="mt-3 text-xs text-text-faint">
                {t("for_agencies.cta_note_prefix")}{" "}
                <span className="font-semibold text-brand-500">
                  {t("for_agencies.cta_note_highlight")}
                </span>
                {t("for_agencies.cta_note_suffix")}
              </p>
            </motion.div>
          </div>

        </div>
      </Container>
    </section>
  );
}
